import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { WrapperToolbarButton, ToolbarButton } from "./styles";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { svgFilter } from "./images";

const IconButton = styled.div`
  width: 18px;
  height: 18px;

  svg {
    width: 90%;
    height: 90%;
    path {
      stroke: var(--Text-Primary);
      fill: var(--Text-Primary);
    }
  }
`;

const FilterContentWrapper = styled.div`
  background: var(--modal-background);
  top: -30px;
  position: relative;
  border-radius: 5px;
  width: auto;
  height: auto;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.21) !important;
  padding: 0px;
  z-index: 1000;
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterRows = styled.div`
  padding: 20px 20px 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  margin-left: 0px;
  margin-top: 0px;
  height: 36px;
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FooterButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-transform: uppercase;
  svg {
    width: 18px;
    height: 18px;
    margin-right: 10px;
  }
  div {
    display: flex;
    align-items: flex-end;
    height: 18px;
    font-size: 11px !important;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .filter-name {
    font-family: "AllianceNo2-Regular" !important;
    padding: 0 0 8px;
    color: var(--Text-Primary);
  }
  .selector {
    height: 36px;
  }
  .logical-selector {
   .MuiSelect-select {
      font-family: "AllianceNo2-Regular" !important;
   }
  }
`;

const SearchItem = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  input[type="search"] {
    border-radius: 0;
    box-shadow: none;
    font-size: 12px;
    border: 1px solid #aaa;
    border-radius: 3px;
    padding: 5px;
    color: var(--Text-Primary);
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  label {
    font-family: "AllianceNo2-Regular" !important;
    font-weight: 100;
    color: var(--Text-Primary);
    margin-right: 5px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: top;
  justify-content: center;
  flex-direction: column;
  margin-top: 20px;
  width: 30px;
  height: auto;
`;

const Footer = styled.div`
  border-top: 1px solid var(--card-header-border-color);
  padding: 10px 20px 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogicalOperatorWrapper = styled.div`
  display: flex;
  height: 36px;
  padding-top: 0;
  padding-bottom: 6px;
  box-sizing: border-box;
  align-items: center;
`;

const operatorStringsOptions = [
  { value: "contains", label: "contains" },
  { value: "containsNot", label: "does not contain" },
  { value: "equals", label: "is equal to" },
  { value: "equalsNot", label: "is not equal to" },
  { value: "endsWith", label: "ends with" },
  { value: "startsWith", label: "starts with" },
];

const operatorNumericsOptions = [
  { label: "=", value: "equalsNr" },
  { label: "<", value: "smaller" },
  { label: "<=", value: "smallerOrEqual" },
  { label: ">=", value: "biggerOrEqual" },
  { label: ">", value: "bigger" },
  { label: "!=", value: "equalsNotNr" },
];

const logicalOperatorsOptions = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

const operatorsNumeric = new Set(
  operatorNumericsOptions.map((opt) => opt.value)
);
const operatorsStrings = new Set(
  operatorStringsOptions.map((opt) => opt.value)
);

function TableFilter({
  columns,
  openFilter,
  setOpenFilter,
  filters,
  setFilters,
  logicalOperator,
  setLogicalOperator,
}) {
  const filterDefault = [
    {
      column: columns[0].id,
      operator:
        columns[0].numeric === true
          ? operatorNumericsOptions[0].value
          : operatorStringsOptions[0].value,
      value: "",
    },
  ];
  const filtersDisplay = filters.length > 0 ? filters : filterDefault;
  const popperRef = useRef(null);
  const handleFilterClick = (event) => {
    setOpenFilter(openFilter ? false : true);
  };
  const columnOptions = columns.map((column) => {
    return { label: column.label, value: column.id };
  });

  const handleClickAway = (e) => {
    setOpenFilter(false);
  };

  function handleColumnFilterChange(e, filter, index) {
    const newFilters = [...filtersDisplay];
    const selectedColumn = columns.find(
      (opt) => opt.id === e.target.value
    );
    const { numeric } = selectedColumn || {};
    const value = operatorsNumeric.has(filter.operator) !== numeric
      ? ""
      : filter.value;
    const operator = numeric === true
      ? operatorNumericsOptions[0].value
      : operatorStringsOptions[0].value;
    newFilters[index] = {
      ...filter,
      column: e.target.value,
      operator,
      value,
    };
    setFilters(newFilters);
  };

  function handleAddFilter() {
    const firstColumn = columns[0];
    const { id: column, numeric } = firstColumn || {};
    const operator = numeric === true
      ? operatorNumericsOptions[0].value
      : operatorStringsOptions[0].value;
    setFilters([
      ...filtersDisplay,
      { column, operator, value: "" },
    ]);
  };

  return (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleClickAway}>
      <WrapperToolbarButton>
        <ToolbarButton
          onClick={handleFilterClick}
          aria-describedby="simple-popper"
          ref={popperRef}
        >
          <span>Filter:</span>
          <IconButton>{svgFilter}</IconButton>
        </ToolbarButton>
        <Popper
          id="simple-popper"
          open={openFilter}
          anchorEl={popperRef.current}
          placement="bottom"
          onMouseUp={(e) => e.stopPropagation()}
        >
          <FilterContentWrapper>
            <FilterRows>
              <FilterWrapper>
                <div className="filter-name">Column</div>
                {filtersDisplay.map((filter, index) => {
                  const { column } = filter;
                  return (
                    <SelectInputWithIcon
                      key={`column-filter-${index}`}
                      title="Column"
                      name="column"
                      className="selector"
                      selected={column}
                      onChange={(e) => {
                        handleColumnFilterChange(e, filter, index);
                      }}
                      options={columnOptions}
                    />
                  );
                })}
              </FilterWrapper>
              <FilterWrapper>
                <div className="filter-name">Operator</div>
                {filtersDisplay.map((filter, index) => {
                  const { operator } = filter;
                  return (
                    <SelectInputWithIcon
                      key={`operator-filter-${index}`}
                      title="operator"
                      name="operator"
                      className="selector"
                      selected={operator}
                      onChange={(e) => {
                        const newFilters = [...filtersDisplay];
                        newFilters[index] = {
                          ...filter,
                          operator: e.target.value,
                        };
                        setFilters(newFilters);
                      }}
                      options={
                        operatorsNumeric.has(operator)
                          ? operatorNumericsOptions
                          : operatorStringsOptions
                      }
                    />
                  );
                })}
              </FilterWrapper>
              <SearchWrapper>
                <label>Value</label>
                {filtersDisplay.map((filter, index) => {
                  const { value } = filter;
                  return (
                    <SearchItem key={`search-filter-${index}`}>
                      <input
                        type="search"
                        id={`table-search-filter-${index}`}
                        name="q"
                        onChange={({ target }) => {
                          const newFilters = [...filtersDisplay];
                          newFilters[index] = {
                            ...filter,
                            value: target.value,
                          };
                          setFilters(newFilters);
                        }}
                        value={value}
                      />
                    </SearchItem>
                  );
                })}
              </SearchWrapper>
              {filtersDisplay?.length > 1 ? (
                <FilterWrapper>
                  <div className="filter-name">
                    <SelectInputWithIcon
                      title="Logical Operator"
                      name="logical-operator"
                      className="logical-selector"
                      selected={logicalOperator}
                      onChange={(e) => {
                        setLogicalOperator(e.target.value);
                      }}
                      options={logicalOperatorsOptions}
                    />
                  </div>
                  {filtersDisplay.map((filter, index) => {
                    return (
                      <LogicalOperatorWrapper key={`logical-op-${index}`}>
                        {index === filtersDisplay.length - 1
                          ? null
                          : logicalOperator}
                      </LogicalOperatorWrapper>
                    );
                  })}
                </FilterWrapper>
              ) : null}
              <ButtonWrapper>
                {filtersDisplay.map((filter, index) => {
                  return (
                    <CloseButton
                      key={`delete-filter-${index}`}
                      className="btn btn-link waves-effect shadow-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilters(filters.filter((_, i) => i !== index));
                        if (filters.length === 1) {
                          setOpenFilter(false);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </CloseButton>
                  );
                })}
              </ButtonWrapper>
            </FilterRows>
            <Footer>
              <FooterButton
                className="btn btn-link waves-effect shadow-none"
                onClick={() => {
                  handleAddFilter();
                }}
              >
                <AddCircleIcon />
                <div>Add Filter</div>
              </FooterButton>
              <FooterButton
                className="btn btn-link waves-effect shadow-none"
                onClick={() => setFilters([])}
              >
                <DeleteForeverIcon />
                <div>Remove All Filters</div>
              </FooterButton>
            </Footer>
          </FilterContentWrapper>
        </Popper>
      </WrapperToolbarButton>
    </ClickAwayListener>
  );
}

export default TableFilter;
